import React, { useState, useEffect } from 'react';
import { 
  LightBulbIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import dashboardService from '../services/dashboardService';
import { useAuthContext } from '../../../features/auth/context/AuthContext';

/**
 * InsightsPanel Component
 * 
 * Displays comprehensive AI/ML-powered financial insights
 * Synthesizes data from Income (gross/net/projected/actual), Budgets, Expenses, and Transactions
 * 
 * @param {Object} props - Component props
 * @param {string} props.dataView - Data view: 'personal', 'partner', or 'combined'
 * @returns {JSX.Element} InsightsPanel component
 */
const InsightsPanel = ({ dataView = 'combined' }) => {
  const { user } = useAuthContext();
  const [insights, setInsights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [insightsData, setInsightsData] = useState(null);

  useEffect(() => {
    let abortController = new AbortController();

    const fetchInsights = async () => {
      try {
        setIsLoading(true);
        setError(null);
        console.log('🧠 InsightsPanel: Fetching comprehensive insights...', { dataView });

        const insightsResponse = await dashboardService.getInsights(
          abortController.signal,
          dataView
        );

        console.log('✅ InsightsPanel: Insights received:', {
          insightsCount: insightsResponse?.generatedInsights?.length || 0,
          hasIncomeData: !!insightsResponse?.income,
          hasBudgetData: !!insightsResponse?.budget,
          hasTransactionData: !!insightsResponse?.transactions
        });

        setInsightsData(insightsResponse);
        setInsights(insightsResponse?.generatedInsights || []);
      } catch (err) {
        if (err.name === 'CanceledError' || err.name === 'AbortError') {
          console.log('🛑 InsightsPanel: Request aborted');
          return;
        }
        console.error('❌ InsightsPanel: Error fetching insights:', err);
        setError(err.message || 'Failed to load financial insights');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInsights();

    return () => {
      abortController.abort();
    };
  }, [dataView]);

  const getColorClasses = (type) => {
    const colorMap = {
      success: 'bg-green-50 border-green-200 text-green-800',
      warning: 'bg-orange-50 border-orange-200 text-orange-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800',
      error: 'bg-red-50 border-red-200 text-red-800'
    };
    return colorMap[type] || colorMap.info;
  };

  const getIconColor = (type) => {
    const colorMap = {
      success: 'text-green-600',
      warning: 'text-orange-600',
      info: 'text-blue-600',
      error: 'text-red-600'
    };
    return colorMap[type] || colorMap.info;
  };

  const getIcon = (type) => {
    const iconMap = {
      success: CheckCircleIcon,
      warning: ExclamationTriangleIcon,
      info: InformationCircleIcon,
      error: ExclamationTriangleIcon
    };
    return iconMap[type] || LightBulbIcon;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-4">
          <LightBulbIcon className="h-6 w-6 text-violet-600 animate-pulse" />
          <h3 className="text-lg font-semibold text-gray-900">Financial Insights</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 bg-gray-50 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-4">
          <LightBulbIcon className="h-6 w-6 text-violet-600" />
          <h3 className="text-lg font-semibold text-gray-900">Financial Insights</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto mb-2 text-orange-300" />
          <p className="text-sm">Unable to load insights</p>
          <p className="text-xs mt-1 text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <div className="flex items-center gap-2 mb-4">
        <LightBulbIcon className="h-6 w-6 text-violet-600" />
        <h3 className="text-lg font-semibold text-gray-900">Financial Insights</h3>
      </div>

      {insights.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <LightBulbIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No insights available yet.</p>
          <p className="text-xs mt-1">Add transactions, income, and budgets to see personalized insights.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {insights.map((insight, index) => {
            const Icon = getIcon(insight.type);
            return (
              <div
                key={index}
                className={`border rounded-lg p-4 ${getColorClasses(insight.type)}`}
              >
                <div className="flex gap-3">
                  <Icon className={`h-6 w-6 flex-shrink-0 mt-0.5 ${getIconColor(insight.type)}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-sm">{insight.title}</h4>
                      {insight.priority === 'high' && (
                        <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          High Priority
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-2">{insight.message}</p>
                    <p className="text-xs opacity-90 italic">💡 {insight.action}</p>
                    {insight.category && (
                      <p className="text-xs mt-1 opacity-75">
                        Category: {insight.category}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          💡 Insights are synthesized from your Income, Budgets, Expenses, and Transactions. 
          These recommendations are for informational purposes and may not reflect your complete financial picture.
        </p>
        {insightsData && (
          <div className="mt-2 text-xs text-gray-400 text-center">
            Analyzing {insightsData.income?.activeIncomeCount || 0} income sources, 
            {insightsData.budget?.hasBudget ? ` ${insightsData.budget.budgets} budget(s),` : ' no budgets,'}
            {' '}{insightsData.transactions?.totalTransactions || 0} transactions
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightsPanel;
